import type { CampaignJourneyData } from "../ratings";

export type ContactOpenMode = "pane" | "modal";

export type ProductInterest = {
  vault: boolean;
  operate: boolean;
  shop: boolean;
  forge: boolean;
};

export const PRODUCT_INTEREST_OPTIONS = [
  { key: "vault", label: "Vault" },
  { key: "operate", label: "Operate" },
  { key: "shop", label: "Shop" },
  { key: "forge", label: "Forge" },
] as const;

export interface ContactBrowserTarget {
  contact_id?: string;
  /** Prefill account when creating a contact */
  account_id?: string;
  create?: boolean;
}

export type ContactOpenState = {
  target: ContactBrowserTarget;
  mode: ContactOpenMode;
};

export interface BusinessCardImage {
  blob_path?: string | null;
  blob_url?: string | null;
  /** Read SAS (or equivalent) the CRM can put in an img src. */
  url?: string | null;
  content_type?: string | null;
  width?: number | null;
  height?: number | null;
  byte_size?: number | null;
  sha256?: string | null;
  expires_at?: string | null;
}

export interface BusinessCard {
  card_id: string;
  contact_id?: string | null;
  account_id?: string | null;
  side?: string | null;
  processed?: BusinessCardImage | null;
  original?: BusinessCardImage | null;
  review_status?: string | null;
  captured_at?: string | null;
}

export interface ContactPhoto {
  blob_path?: string | null;
  blob_url?: string | null;
  /** Read SAS (or equivalent) the CRM can put in an img src. */
  url?: string | null;
  content_type?: string | null;
  source_url?: string | null;
  source_type?: string | null;
  width?: number | null;
  height?: number | null;
  byte_size?: number | null;
  sha256?: string | null;
  captured_at?: string | null;
  actor?: string | null;
  lineage?: string | null;
  expires_at?: string | null;
}

export interface ContactRecord {
  contact_id: string;
  account_id: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  title: string | null;
  phone: string | null;
  profile_url?: string | null;
  source_url?: string | null;
  photo?: ContactPhoto | null;
  business_cards?: BusinessCard[] | null;
  consent_basis: string | null;
  event_contact_no?: number | null;
  event_contact_display?: string | null;
  do_not_contact?: boolean | null;
  event_context?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  address_line3?: string | null;
  city?: string | null;
  state_region?: string | null;
  postal_code?: string | null;
  country_code?: string | null;
  country_raw?: string | null;
  raw_attributes?: Record<string, unknown> | null;
  my_favorite?: boolean;
}

export interface ContactDossier {
  contact: ContactRecord;
  account_name: string | null;
  segment_primary: string | null;
  campaign: CampaignJourneyData;
  booth: string | null;
  hall: string | null;
}

export type ContactDraft = {
  account_id: string;
  email: string;
  first_name: string;
  last_name: string;
  title: string;
  phone: string;
  consent_basis: string;
  source_url: string;
  do_not_contact: boolean;
  product_interest: ProductInterest;
  address_line1: string;
  address_line2: string;
  address_line3: string;
  city: string;
  state_region: string;
  postal_code: string;
  country_code: string;
  country_raw: string;
};

export const CONSENT_BASIS_OPTIONS = [
  { value: "", label: "—" },
  { value: "crm", label: "CRM" },
  { value: "legitimate_interest", label: "Legitimate interest" },
  { value: "badge_scan", label: "Badge scan" },
  { value: "public_professional", label: "Public professional" },
] as const;
